import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  RefreshCw,
  TrendingUp,
  CalendarDays,
  BarChart3,
  Receipt,
  Stethoscope,
  Pill,
  FileText,
} from "lucide-react";

const nowYear = new Date().getFullYear();

function money(v) {
  return `₱${Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-5 flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
          <Icon size={20} />
        </div>
        <div>
          <div className="text-2xl font-semibold text-slate-900">{value}</div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{label}</div>
          {sub ? <div className="text-xs text-slate-500 mt-1">{sub}</div> : null}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DoctorBusiness() {
  const [year, setYear] = useState(nowYear);
  const [period, setPeriod] = useState("monthly");
  const [summary, setSummary] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/doctor-business/sales-summary", { params: { year } }),
        api.get("/doctor-business/sales-report", { params: { year, period } }),
      ]);
      setSummary(s.data);
      setReport(r.data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, period]);

  const yearOptions = [nowYear - 2, nowYear - 1, nowYear, nowYear + 1];

  return (
    <div className="p-6 md:p-8 max-w-[1500px]" data-testid="doctor-business-page">
      <div className="mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>
            Doctor Business Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Track clinic sales by month, quarter, and year. This reads existing POS transaction data only.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <select
            className="h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>

          <select
            className="h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
          </select>

          <Button variant="outline" onClick={load} disabled={busy}>
            <RefreshCw size={15} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard icon={CalendarDays} label="Today" value={money(summary?.today?.total)} sub={`${summary?.today?.transactions || 0} transactions`} />
        <StatCard icon={TrendingUp} label="This Month" value={money(summary?.month?.total)} sub={`${summary?.month?.transactions || 0} transactions`} />
        <StatCard icon={BarChart3} label={`Q${summary?.quarter_number || ""}`} value={money(summary?.quarter?.total)} sub={`${summary?.quarter?.transactions || 0} transactions`} />
        <StatCard icon={Receipt} label={String(summary?.year_number || year)} value={money(summary?.year?.total)} sub={`${summary?.year?.transactions || 0} transactions`} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5">
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 size={16} />
              {period.charAt(0).toUpperCase() + period.slice(1)} Sales Report
            </CardTitle>
          </CardHeader>

          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 border-b">
                  <tr>
                    <th className="p-3 text-left">Period</th>
                    <th className="p-3 text-right">Total Sales</th>
                    <th className="p-3 text-right">Services</th>
                    <th className="p-3 text-right">Medicines / Products</th>
                    <th className="p-3 text-right">Certificates</th>
                    <th className="p-3 text-right">Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  {(report?.rows || []).map((r) => (
                    <tr key={r.period} className="border-b last:border-0">
                      <td className="p-3 font-medium text-slate-900">{r.period}</td>
                      <td className="p-3 text-right font-semibold text-slate-900">{money(r.total)}</td>
                      <td className="p-3 text-right text-slate-700">{money(r.services)}</td>
                      <td className="p-3 text-right text-slate-700">{money(r.medicines)}</td>
                      <td className="p-3 text-right text-slate-700">{money(r.certificates)}</td>
                      <td className="p-3 text-right text-slate-700">{r.transactions || 0}</td>
                    </tr>
                  ))}
                  {(report?.rows || []).length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-10 text-center text-slate-400">
                        No sales data found for this period.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
                {report?.totals ? (
                  <tfoot className="bg-slate-50 border-t">
                    <tr>
                      <td className="p-3 font-semibold">Total</td>
                      <td className="p-3 text-right font-bold">{money(report.totals.total)}</td>
                      <td className="p-3 text-right font-semibold">{money(report.totals.services)}</td>
                      <td className="p-3 text-right font-semibold">{money(report.totals.medicines)}</td>
                      <td className="p-3 text-right font-semibold">{money(report.totals.certificates)}</td>
                      <td className="p-3 text-right font-semibold">{report.totals.transactions || 0}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-base">Revenue Mix</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <Stethoscope size={15} className="text-blue-600" />
                  Services
                </div>
                <div className="font-semibold">{money(summary?.year?.services)}</div>
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <Pill size={15} className="text-emerald-600" />
                  Medicines / Products
                </div>
                <div className="font-semibold">{money(summary?.year?.medicines)}</div>
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <FileText size={15} className="text-purple-600" />
                  Certificates
                </div>
                <div className="font-semibold">{money(summary?.year?.certificates)}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 text-sm text-amber-800">
              This module is currently read-only and uses existing paid POS transactions. If sales look low, the usual cause is unpaid or non-POS transactions not yet encoded.
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
